import { useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  AlignLeft,
  Quote,
  Link2,
  Image as ImageIcon,
  Video,
  Table,
  MoreHorizontal,
  Maximize2,
  ChevronDown,
} from "lucide-react";

export default function BlogEditor({ onContentChange, initialContent = "" }) {
  const editorRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(!initialContent);
  const [wordCount, setWordCount] = useState(0);
  const [expanded, setExpanded] = useState(false);

  const handleInput = () => {
    const text = editorRef.current?.innerText || "";
    const trimmed = text.trim();
    setIsEmpty(trimmed.length === 0);
    setWordCount(trimmed.length === 0 ? 0 : trimmed.split(/\s+/).length);
    onContentChange?.(editorRef.current?.innerHTML || "");
  };

  useEffect(() => {
    if (initialContent && editorRef.current) {
      editorRef.current.innerHTML = initialContent;
      handleInput();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exec = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    handleInput();
  };

  const handleLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  const handleImage = () => {
    const url = window.prompt("Enter image URL");
    if (url) exec("insertImage", url);
  };

  const toolbarButtons = [
    { icon: Bold, label: "Bold", onClick: () => exec("bold") },
    { icon: Italic, label: "Italic", onClick: () => exec("italic") },
    { icon: Underline, label: "Underline", onClick: () => exec("underline") },
    {
      icon: Strikethrough,
      label: "Strikethrough",
      onClick: () => exec("strikeThrough"),
    },
    {
      icon: List,
      label: "Bullet list",
      onClick: () => exec("insertUnorderedList"),
    },
    {
      icon: ListOrdered,
      label: "Numbered list",
      onClick: () => exec("insertOrderedList"),
    },
    { icon: AlignLeft, label: "Align left", onClick: () => exec("justifyLeft") },
    {
      icon: Quote,
      label: "Quote",
      onClick: () => exec("formatBlock", "blockquote"),
    },
    { icon: Link2, label: "Insert link", onClick: handleLink },
    { icon: ImageIcon, label: "Insert image", onClick: handleImage },
    { icon: Video, label: "Insert video" },
    { icon: Table, label: "Insert table" },
  ];

  return (
    <div className="bg-white border border-slate-100 shadow-sm rounded-3xl p-6 sm:p-8">
      <h2 className="font-bold text-lg text-slate-900 mb-1">
        Write Your Blog
      </h2>
      <p className="text-sm text-slate-500 mb-5">
        Share your experience, tips, and stories with the community.
      </p>

      <div className="border border-slate-200 rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-1 flex-wrap px-3 py-2 border-b border-slate-200 bg-slate-50">
          <div className="relative">
            <select
              onChange={(e) => exec("formatBlock", e.target.value)}
              defaultValue="p"
              className="appearance-none text-sm font-medium text-slate-600 bg-transparent pl-2 pr-6 py-1.5 rounded-md hover:bg-slate-100 outline-none cursor-pointer"
            >
              <option value="p">Paragraph</option>
              <option value="h1">Heading 1</option>
              <option value="h2">Heading 2</option>
              <option value="h3">Heading 3</option>
            </select>
            <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          <span className="w-px h-5 bg-slate-200 mx-1" />

          {toolbarButtons.map(({ icon: Icon, label, onClick }) => (
            <button
              key={label}
              type="button"
              aria-label={label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={onClick}
              className="w-8 h-8 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-[#7209B7] transition-colors"
            >
              <Icon className="w-4 h-4" />
            </button>
          ))}

          <button
            type="button"
            aria-label="More options"
            className="w-8 h-8 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-[#7209B7] transition-colors"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          <button
            type="button"
            aria-label="Toggle fullscreen"
            onClick={() => setExpanded((prev) => !prev)}
            className="w-8 h-8 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-[#7209B7] transition-colors ml-auto"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Editable area */}
        <div className="relative">
          {isEmpty && (
            <p className="absolute top-4 left-4 text-sm text-slate-400 pointer-events-none">
              Start writing your amazing travel story...
            </p>
          )}
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            className={`prose prose-sm max-w-none px-4 py-4 outline-none text-sm text-slate-800 overflow-y-auto transition-all duration-200 ${
              expanded ? "min-h-[70vh]" : "min-h-[280px]"
            }`}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-200 bg-slate-50 text-xs text-slate-400">
          <span>Words: {wordCount}</span>
          <span>Powered by TipTap</span>
        </div>
      </div>
    </div>
  );
}
